import { User, Calendar, Mail, Phone, MapPin, Shield, CheckCircle, XCircle } from 'lucide-react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { User as UserType, UserRole, UserStatus } from '@/types';

interface UserDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserType | null;
}

export default function UserDetailsModal({
  isOpen,
  onClose,
  user,
}: UserDetailsModalProps) {
  if (!user) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: UserStatus) => {
    switch (status) {
      case UserStatus.ACTIVE:
        return 'bg-green-50 text-green-700 border-green-100';
      case UserStatus.INACTIVE:
        return 'bg-gray-50 text-gray-700 border-gray-100';
      case UserStatus.SUSPENDED:
        return 'bg-red-50 text-red-700 border-red-100';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-100';
    }
  };

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case UserRole.EMPLOYER:
        return 'bg-blue-50 text-blue-700 border-blue-100';
      case UserRole.AGENT:
        return 'bg-purple-50 text-purple-700 border-purple-100';
      case UserRole.SUPER_ADMIN:
        return 'bg-orange-50 text-orange-700 border-orange-100';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-100';
    }
  };

  const formatRole = (role: UserRole) => {
    switch (role) {
      case UserRole.EMPLOYER:
        return 'Employer';
      case UserRole.AGENT:
        return 'Agent';
      case UserRole.SUPER_ADMIN:
        return 'Super Admin';
      default:
        return role;
    }
  };

  const getInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <div className="py-6">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-2xl font-bold text-gray-900">User Details</h2>
              <Badge
                variant="outline"
                className={`${getStatusColor(user.status)} px-3 py-1 rounded-lg font-medium capitalize`}
              >
                {user.status.toLowerCase()}
              </Badge>
            </div>
            <p className="text-sm text-gray-600">View and manage user information</p>
          </div>

          {/* User Profile Card */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 mb-6 text-white">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                <span className="text-2xl font-bold">
                  {getInitials(user.firstName, user.lastName)}
                </span>
              </div>
              <div>
                <h3 className="text-xl font-bold">
                  {user.firstName} {user.lastName}
                </h3>
                <Badge
                  variant="outline"
                  className={`${getRoleColor(user.role)} mt-1`}
                >
                  {formatRole(user.role)}
                </Badge>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <User className="w-4 h-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Contact Information</h3>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-gray-400" />
                <div className="flex-1">
                  <span className="text-xs text-gray-600">Email</span>
                  <p className="text-sm font-medium text-gray-900">{user.email}</p>
                </div>
                <div className="flex items-center gap-1">
                  {user.isEmailVerified ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-xs text-green-600">Verified</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4 text-red-600" />
                      <span className="text-xs text-red-600">Not Verified</span>
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-gray-400" />
                <div className="flex-1">
                  <span className="text-xs text-gray-600">Phone</span>
                  <p className="text-sm font-medium text-gray-900">{user.phoneNumber || 'N/A'}</p>
                </div>
                {user.phoneNumber && (
                  <div className="flex items-center gap-1">
                    {user.isPhoneVerified ? (
                      <>
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-xs text-green-600">Verified</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 text-red-600" />
                        <span className="text-xs text-red-600">Not Verified</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-4 h-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Additional Information</h3>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              {user.companyName && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Company</span>
                  <span className="text-sm font-medium text-gray-900">{user.companyName}</span>
                </div>
              )}
              {user.gender && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Gender</span>
                  <span className="text-sm font-medium text-gray-900 capitalize">{user.gender}</span>
                </div>
              )}
              {user.country && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Country</span>
                  <span className="text-sm font-medium text-gray-900">{user.country}</span>
                </div>
              )}
              {user.state && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">State</span>
                  <span className="text-sm font-medium text-gray-900">{user.state}</span>
                </div>
              )}
              {user.city && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">City</span>
                  <span className="text-sm font-medium text-gray-900">{user.city}</span>
                </div>
              )}
            </div>
          </div>

          {/* Account Security */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-4 h-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Account Security</h3>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">OTP Authentication</span>
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-100">
                  Enabled
                </Badge>
              </div>
            </div>
          </div>

          {/* Activity Timeline */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Activity Timeline</h3>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Date Joined</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatDate(user.createdAt)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Last Updated</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatDateTime(user.updatedAt)}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="sticky bottom-0 bg-white pt-4 border-t border-gray-200 flex gap-3">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1"
            >
              Close
            </Button>
            <Button
              className="flex-1 bg-gray-900 hover:bg-gray-800 text-white"
            >
              Edit User
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
